/**
 * Figure URLs for student take screens — mirrors mobile collectQuestionFigureUrls
 * (image_urls + image_url + visual_image_url) without passage-owned pages.
 */

const imgFileKey = (u) => String(u || '').split('?')[0].split('/').pop();

/**
 * "Tick more than one box" / "more than one correct answer" — not "There may be more than one good
 * answer to some questions: explain your thinking" (Dulwich Creative Comprehension), which is about
 * open answers and turned every single-answer MCQ in its section into tick boxes.
 * Mirrors StudentPaperMcqInferenceService::moreThanOneMeansSeveralTicks.
 */
export function moreThanOneMeansSeveralTicks(text) {
  const plain = String(text || "");
  return /\bmore than one\b[^.:;]{0,25}\b(?:correct|right|true)\b/i.test(plain)
    || /\b(?:tick|select|choose|circle|pick|shade|mark)\b[^.:;]{0,25}\bmore than one\b/i.test(plain);
}

/**
 * @param {object|null|undefined} question
 * @param {{ includeHtmlEmbedded?: boolean }} [opts]
 * @returns {string[]}
 */
export function collectQuestionFigureUrls(question, opts = {}) {
  if (!question) return [];
  const includeHtmlEmbedded = opts.includeHtmlEmbedded !== false;
  const seen = new Set();
  const result = [];
  const add = (url) => {
    const u = typeof url === 'string' ? url.trim() : '';
    if (!u) return;
    // Dedupe on the FILE, not the URL string. The same asset reaches the client both straight from
    // R2 ("https://pub-….r2.dev/question-bank-images/41/x.png") and through the API proxy
    // ("…/api/proxy/visual/question-bank-images/41/x.png"), so a string-keyed Set let one figure
    // through twice and the question rendered it twice. imgFileKey is the same basename test the
    // strip/overlay helpers in this file already use.
    const key = imgFileKey(u) || u;
    if (seen.has(key)) return;
    seen.add(key);
    result.push(u);
  };

  if (Array.isArray(question.image_urls)) {
    question.image_urls.forEach(add);
  }
  // Empty image_urls: [] must not hide a legacy image_url (mobile parity).
  add(question.image_url);
  add(question.visual_image_url);
  add(question?.answer_data?.visual_image_url);

  if (includeHtmlEmbedded) {
    const html = String(question?.question_text || '');
    const re = /<img[^>]*\ssrc=["']([^"']+)["']/gi;
    let m;
    while ((m = re.exec(html)) !== null) {
      add(m[1]);
    }
  }

  return result;
}

/**
 * Figures the stem's rich-text HTML paints itself (editor-inserted <img>).
 * The stem block renders that HTML, so these count as "already on screen" even
 * though the stem's separate figure strip never lists them.
 *
 * @param {string} html
 * @returns {string[]}
 */
export function htmlEmbeddedFigureUrls(html) {
  const re = /<img[^>]*\ssrc=["']([^"']+)["']/gi;
  const out = [];
  let m;
  while ((m = re.exec(String(html || ''))) !== null) {
    if (m[1]) out.push(m[1]);
  }
  const md = /!\[[^\]]*\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
  while ((m = md.exec(String(html || ''))) !== null) {
    if (m[1]) out.push(m[1]);
  }
  return out;
}

/**
 * Global ownership: if separate figure URLs are shown, strip matching <img> from HTML
 * (and vice versa for the figure strip). Basename identity matches take-screen filters.
 *
 * @param {string} html
 * @param {string[]} figureUrls
 * @returns {string}
 */
export function stripHtmlImagesMatchingFigures(html, figureUrls) {
  const keys = new Set((figureUrls || []).map(imgFileKey).filter(Boolean));
  if (!keys.size) return String(html || '');
  return String(html || '')
    .replace(/<img\b[^>]*>/gi, (tag) => {
      const src = (tag.match(/src=["']([^"']+)["']/i) || [])[1];
      if (!src) return tag;
      return keys.has(imgFileKey(src)) ? '' : tag;
    })
    .replace(/!\[[^\]]*\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (full, src) =>
      keys.has(imgFileKey(src)) ? '' : full
    )
    .trim();
}

/** Two or more pictures written into the HTML, with words after the first one — a laid-out question. */
export function imagesInterleaveText(html) {
  const tags = String(html || '').match(/<img\b[^>]*>/gi) || [];
  if (tags.length < 2) return false;
  const first = String(html).search(/<img\b[^>]*>/i);
  const after = String(html).slice(first + tags[0].length)
    .replace(/<img\b[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ');
  return /\p{L}{3,}/u.test(after);
}

/**
 * Prefer separate image_urls; drop URLs already painted in HTML when HTML is kept.
 * For review we strip HTML embeds instead so the dedicated <img> strip wins once.
 *
 * @param {object|null|undefined} row
 * @returns {{ images: string[], html: string }}
 */
export function reconcileRowFiguresForDisplay(row) {
  const html = String(row?.question_text || row?.group_title || '');
  if (choicePartHidesUnreferencedFigure(row)) {
    const embedded = htmlEmbeddedFigureUrls(html);
    return {
      images: [],
      html: embedded.length ? stripHtmlImagesMatchingFigures(html, embedded) : html,
    };
  }
  const images = collectQuestionFigureUrls(row, { includeHtmlEmbedded: false });
  const embedded = htmlEmbeddedFigureUrls(html);
  if (images.length === 0 && embedded.length === 0) {
    return { images: [], html };
  }
  // Pictures laid out between the paragraphs ("Here are some number cards: [cards] You can make 3927
  // like this: [cards]", NLIGSC G2 2010 Q28) only read in that order — keep them where they were written
  // and leave just the other figures for the strip. Same rule as StemFigureOwnership::reconcile.
  if (imagesInterleaveText(html)) {
    const inlineKeys = new Set(embedded.map(imgFileKey).filter(Boolean));
    return { images: images.filter((u) => !inlineKeys.has(imgFileKey(u))), html };
  }
  if (images.length === 0) {
    return { images: embedded, html: stripHtmlImagesMatchingFigures(html, embedded) };
  }
  return { images, html: stripHtmlImagesMatchingFigures(html, images) };
}

/**
 * Shared parent figures for a grouped leaf when the leaf has no own figure.
 * Avoids double-paint when the stem block already shows the same URLs.
 *
 * @param {object} parent
 * @param {object} sub
 * @param {{ stemShowsParentFigures?: boolean, excludeKeys?: Set<string> }} [opts]
 * @returns {string[]}
 */
const IMPLICIT_GEOMETRY_VERB =
  /^(?:find|calculate|work\s+out|determine|give|state|write\s+down|measure|what\s+is)\b/i;
const IMPLICIT_GEOMETRY_NOUN =
  /\b(?:perimeter|area|volume|circumference|length|width|height|size|angle|diagonal|radius|diameter|side|missing\s+side)\b/i;
const IMPLICIT_GEOMETRY_EXCLUSIVE =
  /\b(?:your|the\s+above|following|below|above|shown|diagram|figure|shape|this|that)\b/i;

function plainQuestionText(html) {
  return String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Short geometry parts that share one printed shape ("Find the area." / "Find the perimeter."). */
export function subsImplyImplicitSharedDiagram(subs) {
  if (!Array.isArray(subs) || subs.length < 2) return false;
  const plains = subs.map((sub) => plainQuestionText(sub?.question_text)).filter(Boolean);
  if (plains.length < 2) return false;
  return plains.every(
    (text) =>
      text.length <= 80 &&
      IMPLICIT_GEOMETRY_VERB.test(text) &&
      IMPLICIT_GEOMETRY_NOUN.test(text) &&
      !IMPLICIT_GEOMETRY_EXCLUSIVE.test(text)
  );
}

/**
 * The one diagram both geometry parts need, including when it was imported only onto part (a).
 * Returns null when the parts have different figures or an image-fill overlay owns the crop.
 */
export function implicitSharedLeafDiagramUrl(parent, subs) {
  if (!subsImplyImplicitSharedDiagram(subs)) return null;
  const fillOwnsFigure = (subs || []).some((sub) => {
    const type = String(sub?.type || sub?.question_type || "").toLowerCase().replace(/-/g, "_");
    if (type !== "image_fill") return false;
    const overlay = sub?.answer_data?.overlay_layout;
    return !overlay || !String(overlay.image_url || "").trim();
  });
  if (fillOwnsFigure) return null;
  const urls = [];
  const add = (url) => {
    const value = String(url || "").trim();
    if (!value) return;
    const key = imgFileKey(value);
    if (urls.some((existing) => imgFileKey(existing) === key)) return;
    urls.push(value);
  };
  collectQuestionFigureUrls(parent, { includeHtmlEmbedded: true }).forEach(add);
  (subs || []).forEach((sub) => {
    collectQuestionFigureUrls(sub, { includeHtmlEmbedded: true }).forEach(add);
  });
  return urls.length === 1 ? urls[0] : null;
}

function partClaimsItsOwnPrintedFigure(plain) {
  const text = String(plain || "").replace(/\s+/g, " ").trim();
  if (!text) return false;
  return (
    /\b(?:this net|from this net|the net|net of a|made from this net)\b/i.test(text) ||
    /\b(?:diagram|figure|graph|chart|table|grids?)\s+(?:below|above|on the right|on the left|shown)\b/i.test(text) ||
    /\bshown\s+(?:below|above|on the right|on the left)\b/i.test(text) ||
    /\b(?:complete|fill(?:\s+in)?|use)\s+the\s+(?:table|grid|chart|graph)\b/i.test(text)
  );
}

/**
 * One context diagram stored only on the first part of a shared story.
 * The stem shows it for every part; the first part must not keep a private copy.
 */
export function soleUnclaimedFirstPartPassageFigure(parent) {
  const subs = Array.isArray(parent?.sub_questions) ? parent.sub_questions : [];
  if (subs.length < 2) return null;
  if (subs.some((sub) => {
    const type = String(sub?.type || sub?.question_type || "").toLowerCase().replace(/-/g, "_");
    if (type !== "image_fill") return false;
    const overlay = sub?.answer_data?.overlay_layout;
    return !overlay || !String(overlay.image_url || "").trim();
  })) {
    return null;
  }
  const stemHtml = String(parent?.question_text || parent?.group_title || "");
  const stemPlain = plainQuestionText(stemHtml);
  const pointsAtSharedDiagram = passageStemPointsAtSharedDiagram(stemHtml);
  if (!pointsAtSharedDiagram) {
    if (stemPlain.length < 80) return null;
    const part0HtmlImages = new Set(
      htmlEmbeddedFigureUrls(subs[0]?.question_text || "").map((u) => imgFileKey(u) || u)
    );
    const sub0Figs = collectQuestionFigureUrls(subs[0], { includeHtmlEmbedded: false });
    if (sub0Figs.some((u) => part0HtmlImages.has(imgFileKey(u) || u))) {
      return null;
    }
  }
  if (
    /\beach\s+of\s+these\b|\bname\s+of\s+each\b|\bfor\s+each\s+(?:shape|figure|diagram|polygon|picture)\b/i.test(stemPlain) ||
    /\bthese\s+(?:shapes?|figures?|polygons?|pictures?|objects?)\b/i.test(stemPlain) ||
    /\b(?:complete|fill(?:\s+in)?)\s+the\s+table\b/i.test(stemPlain) ||
    /\btable\s+below\b/i.test(stemPlain)
  ) {
    return null;
  }
  if (groupedPartFigureUrls(stemHtml, subs.length).some((figs) => figs.length > 0)) return null;
  if (subs.some((sub) => partClaimsItsOwnPrintedFigure(plainQuestionText(sub?.question_text)))) return null;
  const counts = new Map();
  subs.forEach((sub, index) => {
    const seen = new Set();
    collectQuestionFigureUrls(sub, { includeHtmlEmbedded: true }).forEach((url) => {
      const key = imgFileKey(url) || url;
      if (!key || seen.has(key)) return;
      seen.add(key);
      const prev = counts.get(key);
      if (prev) prev.rows += 1;
      else counts.set(key, { url, rows: 1, first: index === 0 });
    });
  });
  if (counts.size !== 1) return null;
  const only = [...counts.values()][0];
  return only.rows === 1 && only.first ? only.url : null;
}

export function passageStemPointsAtSharedDiagram(html) {
  const plain = plainQuestionText(html);
  if (!plain) return false;
  return (
    /\blook\s+at\s+(?:these|the following)\s+angles\b/i.test(plain) ||
    /\b(?:diagram|figure|symbol|scale|graph|chart|display|table|grid)\s+(?:below|above)\b/i.test(plain) ||
    /\bin the (?:diagram|figure|scale|graph|chart)\b/i.test(plain) ||
    /\busing the below\b/i.test(plain) ||
    /\b(?:this|the)\s+(?:bar\s+)?(?:graph|chart)\b/i.test(plain) ||
    /\b(?:bar\s+)?(?:graphs?|charts?)\s+shows?\b/i.test(plain) ||
    passageStemPointsAtPluralSharedCharts(plain) ||
    /\bnumber\s+lines?\b/i.test(plain) ||
    stemIntroducesPrintedPattern(plain) ||
    /\bhere are\s+(?:three|two|four|\d+)\s+patterns?\b/i.test(plain)
  );
}

function passageStemPointsAtPluralSharedCharts(plain) {
  const text = String(plain || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return false;
  if (/\b(?:passage|extract|poem|novel|chapter|comprehension)\b/i.test(text)) return false;
  return (
    /\b(?:charts|graphs)\s+(?:below|above)\b/i.test(text) ||
    /\b(?:use|using)\s+the\s+(?:charts|graphs)\b/i.test(text)
  );
}

function groupedPartFigureUrls(stemHtml, partCount) {
  const parts = Array.from({ length: Math.max(0, partCount) }, () => []);
  if (!stemHtml || parts.length < 2) return parts;
  const html = String(stemHtml).replace(/<\/?(?:strong|b|em|i|span)\b[^>]*>/gi, '');
  const markers = [...html.matchAll(/(?:^|[\s>])\(?([a-z])\)\s*/gi)].flatMap((match) => {
    const index = String(match[1] || '').toLowerCase().charCodeAt(0) - 97;
    return index >= 0 && index < parts.length && match.index != null
      ? [{ index, start: match.index + match[0].length }]
      : [];
  });
  if (markers.length < 2) return parts;
  const prefixPlain = html.slice(0, markers[0].start).replace(/<[^>]+>/g, " ");
  const sharedDiagramIntro = passageStemPointsAtPluralSharedCharts(prefixPlain);
  for (let markerIndex = 0; markerIndex < markers.length; markerIndex += 1) {
    const marker = markers[markerIndex];
    const end = markers[markerIndex + 1]?.start ?? html.length;
    const section = html.slice(marker.start, end);
    const blockEnd = section.search(/<\/(?:p|li|div)\s*>/i);
    const ownedHtml = sharedDiagramIntro && blockEnd >= 0 ? section.slice(0, blockEnd) : section;
    for (const url of htmlEmbeddedFigureUrls(ownedHtml)) {
      const key = imgFileKey(url) || url;
      if (parts[marker.index].some((existing) => (imgFileKey(existing) || existing) === key)) continue;
      parts[marker.index].push(url);
    }
  }
  const claimForPart = (partIndex, url) => {
    if (partIndex < 0 || partIndex >= parts.length) return;
    const key = imgFileKey(url) || url;
    for (const list of parts) {
      const existing = list.findIndex((item) => (imgFileKey(item) || item) === key);
      if (existing >= 0) list.splice(existing, 1);
    }
    parts[partIndex].push(url);
  };
  for (let markerIndex = 0; markerIndex < markers.length; markerIndex += 1) {
    const marker = markers[markerIndex];
    const end = markers[markerIndex + 1]?.start ?? html.length;
    const section = html.slice(marker.start, end);
    const phrase = section.search(/\b(?:now\s+)?look\s+at\s+this\s+diagram\b/i);
    if (phrase < 0) continue;
    const after = htmlEmbeddedFigureUrls(section.slice(phrase));
    const target = marker.index + 1 < parts.length ? marker.index + 1 : marker.index;
    if (after[0]) claimForPart(target, after[0]);
  }
  return parts;
}

function stemIntroducesDistinctCardSets(stemHtml) {
  const plain = String(stemHtml || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
  const countIn = (letter) => {
    const section = plain.match(
      new RegExp(
        `(?:\\(\\s*${letter}\\s*\\)|\\b${letter}\\))([\\s\\S]{0,500}?)(?=(?:\\(\\s*[a-z]\\s*\\)|\\b[a-z]\\))|$)`,
        'i'
      )
    );
    const found = section?.[1]?.match(/\b((?:two|three|four|five|six|seven|eight|nine|ten|\d+)\s+cards?)\b/i);
    return found ? found[1].replace(/\s+/g, ' ').toLowerCase() : null;
  };
  const partA = countIn('a');
  const partB = countIn('b');
  return Boolean(partA && partB && partA !== partB);
}

/** Image in (b), shared by the later parts, must not be painted on part (a). */
export function laterPartSharedFigureFromStem(stemHtml, partCount) {
  const parts = groupedPartFigureUrls(stemHtml, partCount);
  if (parts.length < 3) return null;
  const partA = parts[0] || [];
  const partB = parts[1] || [];
  if (partA.length === 2 && parts.slice(1).every((figs) => figs.length === 0) && stemIntroducesDistinctCardSets(stemHtml)) {
    return { laterSharedFigure: partA[0], partAFigure: partA[1] };
  }
  if (partA.length !== 1 || partB.length !== 1) return null;
  const partAFigure = partA[0];
  const laterSharedFigure = partB[0];
  if ((imgFileKey(partAFigure) || partAFigure) === (imgFileKey(laterSharedFigure) || laterSharedFigure)) {
    return null;
  }
  for (let index = 2; index < parts.length; index += 1) {
    const laterKey = imgFileKey(laterSharedFigure) || laterSharedFigure;
    if ((parts[index] || []).some((url) => (imgFileKey(url) || url) !== laterKey)) return null;
  }
  return { partAFigure, laterSharedFigure };
}

/**
 * The student must sketch their own figure ("justify via sketches or a sentence").
 * A printed diagram from another part is not this question. Keep a figure when this
 * part points at it ("the diagram below", "this triangle").
 */
export function partAsksStudentToSketchWithoutPrintedDiagram(text) {
  const plain = String(text || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return false;
  if (/\b(?:this|the)\s+(?:triangle|shape|figure|diagram|graph|chart|timetable|grid)\b/i.test(plain)) {
    return false;
  }
  if (/\b(?:diagram|figure|shape|grid|graph|chart)\s+(?:below|above)\b/i.test(plain)) return false;
  if (/\bshown\s+(?:below|above)\b/i.test(plain)) return false;
  if (/\bin the (?:diagram|figure|graph|chart|grid)\b/i.test(plain)) return false;
  return (
    /\bvia\s+sketches?\b/i.test(plain) ||
    /\bsketches?\s+or\s+(?:a\s+)?sentences?\b/i.test(plain) ||
    /\bjustify\b[^.]{0,120}\bsketches?\b/i.test(plain)
  );
}

/**
 * "Here is a pattern made with circles and lines" — that picture stays with the
 * sentence, even when a part shades the same file.
 */
export function stemIntroducesPrintedPattern(text) {
  const plain = String(text || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return false;
  return (
    /\bhere is\s+(?:a|the)\s+pattern\b/i.test(plain) ||
    /\bpattern\s+made\s+with\b/i.test(plain)
  );
}

function textRefersToPrintedTable(text) {
  const plain = String(text || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!/\btables?\b/i.test(plain)) return false;
  return /\b(?:below|above|shows|shown|results|missing|complete|fill|use|using|from)\b/i.test(plain);
}

/**
 * A short shared maths prompt ("numbers in rows… shown below", "diagram below")
 * is the question, even when a part is flagged to hide a repeated figure.
 * Plural chart pairs stay with the passage rules and are not forced inline here.
 */
export function printedMathsArrangementStaysOnQuestion(stemHtml, partHtml) {
  const plain = String(stemHtml || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain || plain.length < 12 || plain.length > 220) return false;
  if (/\b(?:passage|extract|poem|novel|chapter|comprehension)\b/i.test(plain)) return false;
  if (/\b(?:charts|graphs)\s+(?:below|above)\b/i.test(plain)) return false;
  if (/\b(?:use|using)\s+the\s+(?:charts|graphs)\b/i.test(plain)) return false;
  const isArrangement =
    (/\brows?\b/i.test(plain) &&
      /\b(?:arrangement|sequence|pattern)\b/i.test(plain) &&
      /\b(?:shown|below)\b/i.test(plain)) ||
    /\b(?:diagram|figure|graph|chart)\s+(?:below|above)\b/i.test(plain);
  if (!isArrangement) return false;
  const part = String(partHtml || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return !part.includes(plain.toLowerCase());
}

/** Drop another part's fill-in table from a pattern introduction. */
export function omitUnreferencedTableFromPatternStem(html, partPlain) {
  const source = String(html || "");
  const plain = source
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!stemIntroducesPrintedPattern(plain)) return source;
  if (textRefersToPrintedTable(plain) || textRefersToPrintedTable(partPlain)) return source;
  return source.replace(/<table[\s\S]*?<\/table>/gi, "").trim();
}

/**
 * A select-all / "which two…" question whose wording points at a printed picture
 * (nets, "the following", "shown below"). Those figures stay.
 */
export function choiceQuestionNeedsPrintedFigure(questionText) {
  const plain = plainQuestionText(questionText);
  if (!plain) return false;
  if (/\b(?:this|the)\s+(?:triangle|shape|figure|diagram|graph|chart|timetable|grid)\b/i.test(plain)) {
    return true;
  }
  if (/\bin the (?:diagram|figure|graph|chart|grid)\b/i.test(plain)) return true;
  if (/\b(?:diagram|figure|shape|grid|graph|chart)\s+(?:below|above)\b/i.test(plain)) return true;
  if (/\bshown\s+(?:below|above)\b/i.test(plain)) return true;
  if (/\b(?:following|shown|below|above|look at|here is|here are)\b/i.test(plain)) return true;
  return /\b(?:these|the|each|of)\s+(?:\w+\s+){0,4}(?:nets?|shapes?|triangles?|squares?|rectangles?|circles?|diagrams?|figures?|pictures?|photos?|graphs?|charts?|grids?|patterns?|cubes?|polygons?|angles?)\b/i.test(
    plain
  );
}

/**
 * A later part that only counts the ways a process can be repeated does not need
 * the earlier part's arrangement boxes. A part that still points at a picture keeps it.
 */
export function repeatedProcessCountOmitsPrintedFigure(questionText) {
  const plain = plainQuestionText(questionText);
  if (!plain) return false;
  if (choiceQuestionNeedsPrintedFigure(plain)) return false;
  return (
    /\b(?:this process|now repeated|repeated with|same process)\b/i.test(plain) &&
    /\bhow many\b/i.test(plain) &&
    /\bways?\b/i.test(plain)
  );
}

/**
 * "Select all that apply" / "which two…" answered by ticking options. A crop of
 * another part's dotted answer lines is not this question when the wording never
 * points at a picture.
 */
export function choicePartHidesUnreferencedFigure(q) {
  if (!q) return false;
  // A grouped parent still carries every part. Hide only on the select-all leaf.
  if (Array.isArray(q.sub_questions) && q.sub_questions.length > 0) return false;
  if (q.answer_data?.interactive || q.answer_data?.overlay_layout || q.answer_data?.table_layout) {
    return false;
  }
  const subtype = String(q.question_subtype || q.answer_data?.question_subtype || "").toLowerCase();
  if (/\b(?:plot|draw)\b/.test(subtype)) return false;
  const type = String(q.question_type || q.type || q.answer_type || "")
    .toLowerCase()
    .replace(/[\s_]+/g, "-");
  if (type.includes("draw") || type.includes("table")) return false;
  const plain = plainQuestionText([q.question_text, q.question].filter(Boolean).join(" "));
  if (repeatedProcessCountOmitsPrintedFigure(plain)) return true;
  const selectsSeveral =
    type === "multiple-select" ||
    type === "multi-select" ||
    /\bselect all(?:\s+that apply)?\b/i.test(plain) ||
    /\bwhich\s+(?:two|three|four|five)\b/i.test(plain) ||
    moreThanOneMeansSeveralTicks(plain);
  if (!selectsSeveral) return false;
  return !choiceQuestionNeedsPrintedFigure(plain);
}

/** Stem HTML with the unreferenced choice figure removed. */
export function stemHtmlOmittingUnreferencedChoiceFigure(question, html) {
  const raw = String(html || "");
  if (!choicePartHidesUnreferencedFigure(question)) return raw;
  const embedded = htmlEmbeddedFigureUrls(raw);
  return embedded.length ? stripHtmlImagesMatchingFigures(raw, embedded) : raw;
}

/** Remove a printed diagram from a part that asks for the student's own sketch. */
export function dropPrintedDiagramFromSketchPart(sub) {
  if (!sub || !partAsksStudentToSketchWithoutPrintedDiagram(sub.question_text || sub.question || "")) {
    return sub;
  }
  const embedded = htmlEmbeddedFigureUrls(sub.question_text);
  return {
    ...sub,
    question_text: embedded.length
      ? stripHtmlImagesMatchingFigures(sub.question_text, embedded)
      : sub.question_text,
    image_url: null,
    image_urls: [],
  };
}

/**
 * A part converted to a drawing paints the GROUP's figure on its canvas while still storing the crop of
 * that same figure under another filename — the filename tests can't see the two match, so the axes
 * printed twice (Caterham 2016 Q20b). When the canvas figure is the parent's and the part's lone image
 * isn't it, that image is the copy. A part with its own figure to READ also owns the canvas image.
 */
export function dropCanvasCopyFromDrawingPart(parent, sub) {
  const canvasKey = imgFileKey(sub?.answer_data?.interactive?.image_url);
  if (!canvasKey) return sub;
  const keysOf = (q) => [...new Set(
    [...(Array.isArray(q?.image_urls) ? q.image_urls : []), q?.image_url].filter(Boolean).map(imgFileKey)
  )];
  const own = keysOf(sub);
  if (own.length !== 1 || own[0] === canvasKey || !keysOf(parent).includes(canvasKey)) return sub;
  return { ...sub, image_url: null, image_urls: [] };
}

/** Drop the image that belongs to the other side of a part-(a) / later-parts split. */
export function omitLaterPartForeignFigure(parent, sub, partIndex) {
  const subs = Array.isArray(parent?.sub_questions) ? parent.sub_questions : [];
  const split = laterPartSharedFigureFromStem(
    parent?.question_text || parent?.group_title || '',
    subs.length
  );
  if (!split || !sub || partIndex < 0) return sub;
  const hide = partIndex === 0 ? split.laterSharedFigure : split.partAFigure;
  const hideKey = imgFileKey(hide);
  const imageUrls = Array.isArray(sub.image_urls)
    ? sub.image_urls.filter((src) => imgFileKey(src) !== hideKey)
    : sub.image_urls;
  const imageUrl = imgFileKey(sub.image_url) === hideKey ? (imageUrls?.[0] || null) : sub.image_url;
  return {
    ...sub,
    question_text: stripHtmlImagesMatchingFigures(sub.question_text, [hide]),
    image_url: imageUrl,
    image_urls: imageUrls,
  };
}

/** "Using the map above" names the shared diagram, not a sibling's own prompt picture. */
export function leafRefersToSharedDiagramAbove(questionText) {
  const leaf = String(questionText || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!leaf) return false;
  return /\bthe\s+(?:map|diagram|figure|graph|chart|picture|plan|grid)\s+above\b/i.test(leaf);
}

function siblingCanvasImageUrl(sub) {
  const spec = sub?.answer_data?.interactive || sub?.correct_interactive;
  const url = spec && typeof spec === 'object' ? spec.image_url : '';
  return typeof url === 'string' ? url.trim() : '';
}

/**
 * Show the sibling's working diagram when this part says "the map above" and
 * that diagram is a different file from the sibling's own picture.
 */
export function resolveDiagramAboveFigures({
  questionText,
  leafFigures,
  siblingFigureLists,
  siblingCanvasUrls,
  siblingTexts,
}) {
  if (!leafRefersToSharedDiagramAbove(questionText)) return null;
  const plain = (value) => String(value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const keyOf = (url) => imgFileKey(url) || url;
  const canvasUrls = [];
  const canvasKeys = new Set();
  for (const raw of siblingCanvasUrls || []) {
    const url = String(raw || '').trim();
    if (!url) continue;
    const key = keyOf(url);
    if (canvasKeys.has(key)) continue;
    canvasKeys.add(key);
    canvasUrls.push(url);
  }
  if (canvasUrls.length === 0) return null;
  const promptKeys = new Set();
  for (const list of siblingFigureLists || []) {
    for (const url of list) {
      const key = keyOf(url);
      if (!canvasKeys.has(key)) promptKeys.add(key);
    }
  }
  const leafPlain = plain(questionText);
  const siblingNamesAPictureThisPartDoesNot = (siblingTexts || []).some((text) => {
    const named = plain(text).match(/\bthis\s+(signpost|diagram|picture|figure|chart|graph|map)\b/i);
    if (!named) return false;
    return !new RegExp(`\\b${named[1]}\\b`, 'i').test(leafPlain);
  });
  const leafKeys = new Set((leafFigures || []).map((url) => keyOf(url)));
  const main = canvasUrls.filter((url) => !leafKeys.has(keyOf(url)) && !promptKeys.has(keyOf(url)));
  if (main.length === 0) return null;
  if (promptKeys.size === 0 && !siblingNamesAPictureThisPartDoesNot) return null;
  const dropKeys = promptKeys.size > 0 ? promptKeys : leafKeys;
  const kept = (leafFigures || []).filter((url) => !dropKeys.has(keyOf(url)));
  const out = [];
  const seen = new Set();
  for (const url of [...main, ...kept]) {
    const key = keyOf(url);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(url);
  }
  return out;
}

/** Replace a sibling prompt picture with the shared diagram this part actually uses. */
export function applyDiagramAboveFigures(sub, ownImages, resolvedImages) {
  const ownKeys = (ownImages || []).map((src) => imgFileKey(src)).filter(Boolean).join('|');
  const nextKeys = (resolvedImages || []).map((src) => imgFileKey(src)).filter(Boolean).join('|');
  if (!nextKeys || nextKeys === ownKeys) return null;
  const dropped = (ownImages || []).filter(
    (src) => !(resolvedImages || []).some((fig) => imgFileKey(fig) === imgFileKey(src))
  );
  return {
    image_urls: resolvedImages,
    image_url: resolvedImages[0] || null,
    question_text: stripHtmlImagesMatchingFigures(sub?.question_text, dropped),
  };
}

export function figuresForGroupedSub(parent, sub, opts = {}) {
  if (
    partAsksStudentToSketchWithoutPrintedDiagram(sub?.question_text || sub?.question || "") ||
    choicePartHidesUnreferencedFigure(sub)
  ) {
    return [];
  }
  const excludeKeys = opts.excludeKeys || new Set();
  const sharedContextKey = imgFileKey(soleUnclaimedFirstPartPassageFigure(parent));
  const own = collectQuestionFigureUrls(sub, { includeHtmlEmbedded: true }).filter(
    (src) => !excludeKeys.has(imgFileKey(src)) && imgFileKey(src) !== sharedContextKey
  );
  const subs = Array.isArray(parent?.sub_questions) ? parent.sub_questions : [];
  const diagramAbove = resolveDiagramAboveFigures({
    questionText: sub?.question_text || sub?.question || '',
    leafFigures: own,
    siblingFigureLists: subs
      .filter((entry) => entry !== sub)
      .map((entry) => collectQuestionFigureUrls(entry, { includeHtmlEmbedded: true })),
    siblingCanvasUrls: subs
      .filter((entry) => entry !== sub)
      .map((entry) => siblingCanvasImageUrl(entry)),
    siblingTexts: subs
      .filter((entry) => entry !== sub)
      .map((entry) => entry?.question_text || entry?.question || ''),
  });
  if (diagramAbove) {
    return diagramAbove.filter((src) => imgFileKey(src) !== sharedContextKey);
  }
  const split = laterPartSharedFigureFromStem(
    parent?.question_text || parent?.group_title || '',
    subs.length
  );
  if (split && subs.length >= 3) {
    const index = subs.findIndex((entry) => entry === sub || String(entry?.id ?? '') === String(sub?.id ?? ''));
    const sharedKey = imgFileKey(split.laterSharedFigure);
    const partAKey = imgFileKey(split.partAFigure);
    if (index === 0) {
      const kept = own.filter((src) => imgFileKey(src) !== sharedKey);
      return kept.length > 0 ? kept : [split.partAFigure];
    }
    if (index > 0) {
      const kept = own.filter((src) => imgFileKey(src) !== partAKey && imgFileKey(src) !== sharedKey);
      return kept.length > 0 ? kept : [split.laterSharedFigure];
    }
  }
  if (own.length > 0) {
    const parentPlain = String(
      parent?.question_text || parent?.group_title || parent?.passage_stem || ""
    );
    const partPlain = String(sub?.question_text || sub?.question || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ");
    // A rainfall/temperature pair is the shared stimulus, not this part's answer art.
    if (
      own.length >= 2 &&
      passageStemPointsAtPluralSharedCharts(parentPlain) &&
      !/\b(?:diagram|figure|graph|chart|table|grids?)\s+(?:below|above|on the right|on the left|shown)\b/i.test(
        partPlain
      )
    ) {
      return [];
    }
    return own;
  }

  if (opts.stemShowsParentFigures) return [];

  const parentFigs = collectQuestionFigureUrls(parent, { includeHtmlEmbedded: true }).filter(
    (src) => !excludeKeys.has(imgFileKey(src))
  );
  return parentFigs;
}

export { imgFileKey as questionFigureFileKey };
